import React, { useState, useCallback, useEffect } from 'react';
import styled, { stylesheet } from 'astroturf/react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from '../../../../../node_modules/yet-another-react-lightbox/dist/plugins/zoom';
import Captions from '../../../../../node_modules/yet-another-react-lightbox/dist/plugins/captions';
import Download from '../../../../../node_modules/yet-another-react-lightbox/dist/plugins/download';
import { Image } from 'js/social/shared/types';

const MIN_ASPECT_RATIO = 1.5;

const ImageContainer = styled.div<{ border?: boolean }>`
  width: 100%;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  cursor: pointer;

  &.border {
    border: 1px solid $ds-gray-light;
    border-radius: 4px;
  }
`;

const styles = stylesheet`
  .overlay {
    --yarl__color_backdrop: rgb(0 0 0 / 0.85);
  }
`;

type Props = {
  noBorder?: boolean;
  image: Image;
  title?: string;
  caption?: string;
  style?: React.CSSProperties;
};

export const PostImage: React.FC<Props> = ({
  noBorder = false,
  image,
  title,
  caption,
  style = {},
}) => {
  const [showLightbox, setShowLightbox] = useState(false);

  // We need to handle scrolling/keys manually because something
  // else on the site conflicts with the default library behavior.
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowLightbox(false);
    }
  }, []);

  useEffect(() => {
    if (showLightbox) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'visible';
    };
  }, [showLightbox, handleKeyDown]);

  // Short images are okay as-is, but don't let tall images
  // take up lots of real estate in the news feed.
  let aspectRatio = MIN_ASPECT_RATIO;
  if (image.width && image.height) {
    const originalAspectRatio = image.width / image.height;
    aspectRatio = Math.max(originalAspectRatio, MIN_ASPECT_RATIO);
  }

  return (
    <ImageContainer
      border={!noBorder}
      className={'clickable'}
      style={{
        backgroundImage: `url(${image.url})`,
        aspectRatio: aspectRatio.toString(),
        ...style,
      }}
      onClick={() => setShowLightbox(true)}
    >
      <Lightbox
        open={showLightbox}
        slides={[
          {
            src: image.url,
            width: image.width ?? undefined,
            height: image.height ?? undefined,
            title,
            description: caption,
          },
        ]}
        carousel={{ finite: true }}
        controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
        plugins={[Zoom, Captions, Download]}
        zoom={{ maxZoomPixelRatio: 2 }}
        className={styles.overlay}
        close={() => setShowLightbox(false)}
      />
    </ImageContainer>
  );
};
